import { Card } from "@/components/Card"
import { CheckCircleIcon } from "@heroicons/react/24/outline"

interface PlanDetailsProps {
  plan: {
    id: string
    name: string
    tier: string
    features: string[]
  }
  limits: {
    storage_gb: number
    max_games: number
    max_team_members?: number
  }
}

export function PlanDetails({ plan, limits }: PlanDetailsProps) {
  const getTierColor = (tier: string) => {
    // Use subtle ring accents instead of changing background so it fits the dark admin theme
    switch (tier) {
      case "enterprise":
        return "ring-purple-500/30"
      case "pro":
        return "ring-blue-500/30"
      case "basic":
        return "ring-green-500/30"
      default:
        return "ring-border/50"
    }
  }

  const formatLimit = (value: number, suffix: string = "") => {
    // subscription_plans stores "unlimited" as 9999 (seed migration 0035)
    if (value === -1 || value >= 9999) return "Unlimited"
    return `${value}${suffix}`
  }

  return (
    <Card className={`p-6 bg-surface ring-1 ${getTierColor(plan.tier)} rounded-lg`}>
      <div className="space-y-5">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-lg font-semibold">{plan.name}</h3>
            <p className="text-sm text-secondary capitalize">{plan.tier} plan</p>
          </div>
          <span className="inline-flex items-center rounded-full border border-border px-2 py-0.5 text-xs capitalize">
            {plan.tier}
          </span>
        </div>

        {/* Limits grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 border-t border-border pt-4">
          <div>
            <p className="text-xs uppercase tracking-wide text-secondary">Storage</p>
            <p className="font-medium">{formatLimit(limits.storage_gb, " GB")}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-secondary">Games</p>
            <p className="font-medium">{formatLimit(limits.max_games)}</p>
          </div>
          {limits.max_team_members !== undefined && (
            <div>
              <p className="text-xs uppercase tracking-wide text-secondary">Team Members</p>
              <p className="font-medium">{formatLimit(limits.max_team_members)}</p>
            </div>
          )}
        </div>

        {/* Features */}
        {plan.features.length > 0 && (
          <div className="border-t border-border pt-4">
            <p className="text-sm font-medium mb-2">Features</p>
            <ul className="space-y-1">
              {plan.features.map((feature, index) => (
                <li key={index} className="flex items-start space-x-2 text-sm">
                  <CheckCircleIcon className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Card>
  )
}
